import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { CalendarToday, AccessTime, VideoCall, Description, Visibility, Edit, Delete } from '@mui/icons-material';
import colors from '../../../shared-colors';
import PrescriptionCard from './PrescriptionCard';
import Button from '../../../components/Button';
import { generateAiSummary } from '../../../utils/api';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const VisitCard = ({ visit, onVisitClick, onEdit, onDelete, formatDate, formatTime, getVisitTypeLabel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleCardClick = () => {
    onVisitClick(visit);
  };

  const handleJoinMeeting = () => {
    window.open(visit.meetingUrl, '_blank');
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(visit);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (onDelete) {
      setIsDeleting(true);
      try {
        await onDelete(visit._id);
      } catch (error) {
        console.error('Delete failed:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return '#FF9800'; // Orange for pending
      case 'COMPLETED':
        return '#4CAF50'; // Green for completed
      case 'CANCELLED':
        return '#f44336'; // Red for cancelled
      default:
        return colors.grayText;
    }
  };

  const handleGenerateAiSummary = async () => {
    const response = await generateAiSummary({ visitId: visit._id });
  };

  const isMeetingButtonEnabled = () => {
    if (!visit.meetingUrl || !visit.scheduledStart || !visit.scheduledEnd) {
      return false;
    }

    const now = moment();
    const startTime = moment.unix(visit.scheduledStart);
    const endTime = moment.unix(visit.scheduledEnd);

    // Enable button 15 minutes before start time and disable 15 minutes after end time
    const fifteenMinutesBeforeStart = startTime.subtract(15, 'minutes');
    const fifteenMinutesAfterEnd = endTime.add(15, 'minutes');

    return now.isBetween(fifteenMinutesBeforeStart, fifteenMinutesAfterEnd, null, '[]');
  };

  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '1px solid #f0f0f0',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-2px)',
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: colors.mainBlue,
                fontSize: '18px',
                fontWeight: 600,
              }}
            >
              {visit.doctorDetails?.userInfo?.imgUrl || visit.doctorDetails?.userInfo?.firstName?.charAt(0) || 'D'}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: colors.black,
                  mb: 0.5,
                }}
              >
                {visit.doctorDetails?.userInfo?.firstName} {visit.doctorDetails?.userInfo?.lastName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.grayText,
                }}
              >
                {visit.eventDetails?.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: '600', color: '#333', mr: 1 }}>
                  {t('status')}:
                </Typography>
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(visit.status),
                    mr: 0.75,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: getStatusColor(visit.status),
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}
                >
                  {visit.status}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('viewDetails')}>
                <IconButton
                  size="small"
                  onClick={handleCardClick}
                  sx={{
                    color: colors.mainBlue,
                    '&:hover': {
                      backgroundColor: colors.lightBlue,
                    },
                  }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <CalendarToday sx={{ fontSize: 16, color: colors.grayText }} />
            <Typography variant="body2" sx={{ color: colors.grayText }}>
              {formatDate(visit.scheduledStart)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <AccessTime sx={{ fontSize: 16, color: colors.grayText }} />
            <Typography variant="body2" sx={{ color: colors.grayText }}>
              {formatTime(visit.scheduledStart)} - {formatTime(visit.scheduledEnd)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Description sx={{ fontSize: 16, color: colors.grayText }} />
            <Typography variant="body2" sx={{ color: colors.grayText }}>
              {getVisitTypeLabel(visit.visitType)}
            </Typography>
          </Box>
        </Box>

        {visit.symptoms && visit.symptoms.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: colors.grayText, mb: 1 }}>
              {t('symptoms')}:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {visit.symptoms.map((symptom, index) => (
                <Chip
                  key={index}
                  label={symptom}
                  size="small"
                  sx={{
                    backgroundColor: colors.lightBlue,
                    color: colors.mainBlue,
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {visit.diagnosis && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: colors.grayText, mb: 1 }}>
              {t('diagnosis')}:
            </Typography>
            <Typography variant="body2" sx={{ color: colors.black }}>
              {visit.diagnosis}
            </Typography>
          </Box>
        )}

        {visit.prescriptions && visit.prescriptions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: colors.grayText, mb: 1 }}>
              {t('prescriptions')}:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {visit.prescriptions.map((prescription, index) => (
                <PrescriptionCard key={index} prescription={prescription} />
              ))}
            </Box>
          </Box>
        )}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            text={t('generateAiSummary')}
            sx={{
              color: colors.black,
              opacity: visit?.status === 'COMPLETED' ? 1 : 0.5,
              cursor: visit?.status === 'COMPLETED' ? 'pointer' : 'not-allowed',
            }}
            onClick={visit?.status === 'COMPLETED' ? handleGenerateAiSummary : undefined}
            disabled={visit?.status !== 'COMPLETED'}
          />
        </Box>

        {visit.meetingUrl && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: isMeetingButtonEnabled() ? 'pointer' : 'not-allowed',
              opacity: isMeetingButtonEnabled() ? 1 : 0.5,
            }}
            onClick={isMeetingButtonEnabled() ? handleJoinMeeting : undefined}
          >
            <VideoCall
              sx={{
                fontSize: 16,
                color: isMeetingButtonEnabled() ? colors.mainBlue : colors.grayText,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: isMeetingButtonEnabled() ? colors.mainBlue : colors.grayText,
                fontWeight: 500,
              }}
            >
              {t('videoConsultationAvailable')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default VisitCard;
